/* eslint-disable camelcase */
import { isClientSide } from 'src/utils/constants';
import { getAPILocale } from 'src/utils/localization';
import axios from 'axios';
import {
  cookieHelper,
  largeExpirationCookieOpts,
  safeJSONParse,
  selectedLocationCookieName,
  zustandAuthorizationStorageName
} from 'src/utils/cookieHelper';
import { requestParams, getAPIDomain } from './constants';
import handleAPIError from './errors';
import { citiesAPI } from './requests/citiesAPI';
import { FetchAttr, UserLocation } from './types';
import { fetchCache } from './caching/cacheUtils';

const getUserIP = async (attr: FetchAttr): Promise<string> => {
  if (attr && attr.ip) {
    return attr.ip;
  }
  return '';
};

const mapDataObject = (data: Record<string, unknown>) => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      // undefined is not a valid value for API. Replace it with null
      return [key, value === undefined ? null : value];
    })
  );
};

export const retrieveUserLocationCookieData = async (
  attr: FetchAttr
): Promise<UserLocation> => {
  const ssrCookies = attr?.cookies;
  if (attr?.locationOverride) {
    // skip cookies manage if location override is provided
    return attr.locationOverride;
  }
  try {
    let userLocation = null;
    // try to find a location in cookies first of all
    if (ssrCookies && ssrCookies[selectedLocationCookieName]) {
      userLocation = unescape(ssrCookies[selectedLocationCookieName]);
    } else if (isClientSide) {
      userLocation = cookieHelper.getCookie(selectedLocationCookieName);
    }
    // no location in cookies has been  found for server and client sides
    if (!userLocation) {
      const ip = await getUserIP(attr);
      const locationByIP = await citiesAPI({ ip });
      const nearestCity = locationByIP?.find((city) => city.nearest);
      // retrieve location with citiesAPI by IP
      if (isClientSide) {
        cookieHelper.setCookie(
          selectedLocationCookieName,
          JSON.stringify(nearestCity),
          largeExpirationCookieOpts.expires
        );
      }
      return nearestCity;
    }

    return safeJSONParse(userLocation);
  } catch (error) {
    handleAPIError(error);
    return null;
  }
};

const retrieveUserTokenCookieData = async (
  attr: FetchAttr
): Promise<string> => {
  const ssrCookies = attr?.cookies;
  try {
    let cookieToken = null;
    // try to find a location in cookies first of all
    if (ssrCookies && ssrCookies[zustandAuthorizationStorageName]) {
      const serverAuthZustandStore = safeJSONParse(
        unescape(ssrCookies[zustandAuthorizationStorageName])
      );
      cookieToken = serverAuthZustandStore?.state?.userToken;
    } else if (isClientSide) {
      const authZustandStore = safeJSONParse(
        cookieHelper.getCookie(zustandAuthorizationStorageName)
      );
      cookieToken = authZustandStore?.state?.userToken;
    }
    return cookieToken;
  } catch (error) {
    handleAPIError(error);
    return null;
  }
};

const requestUrl = (
  url: string,
  city: string,
  latitude: string,
  longitude: string,
  attr?: FetchAttr,
  queryParams?: { [key: string]: string }
): string => {
  const paramsString = new URLSearchParams({
    ...(attr?.shareLatitudeLongitude &&
      latitude && { latitude: latitude?.toString() }),
    ...(attr?.shareLatitudeLongitude &&
      longitude && { longitude: longitude?.toString() }),
    ...(city && { city }),
    locale: getAPILocale(attr),
    ...(queryParams && { ...queryParams })
    // Pass recaptcha only if provided
    // ...(attr?.recaptchaToken && { reToken: getReCaptchaToken(attr) })
  });
  const connectSymbol = url?.includes('?') ? '&' : '?';
  return `${getAPIDomain()}${url}${connectSymbol}${paramsString.toString()}`;
};

const makeAxiosRequest = async (requestConfig, attr?: FetchAttr) => {
  const params = new URL(requestConfig.url).searchParams;
  const locale = params.get('locale');
  try {
    const response = await axios(requestConfig);
    if (response.status >= 200) {
      return response.data;
    }
    throw response;
  } catch (error) {
    if (error?.response?.status === 401) {
      cookieHelper.eraseCookie(zustandAuthorizationStorageName);
      if (isClientSide && window.location.href !== window.location.origin) {
        window.location.href = window.location.origin;
      }
    }
    handleAPIError(
      error,
      {
        url: requestConfig.url,
        attr
      },
      locale
    );
  }
};

export const postData = async (
  url: string,
  data?,
  attr?: FetchAttr,
  method?: string
) => {
  const location = await retrieveUserLocationCookieData(attr);
  const token = attr?.authRequired
    ? await retrieveUserTokenCookieData(attr)
    : null;

  const requestConfig = {
    ...requestParams(attr?.authRequired, token),
    url: requestUrl(
      url,
      location?.name,
      location?.latitude,
      location?.longitude,
      attr
    ),
    method: method || 'POST',
    data: data ? JSON.stringify(mapDataObject(data)) : ''
  };

  if (attr?.useCache) {
    return fetchCache(requestConfig, () =>
      makeAxiosRequest(requestConfig, attr)
    );
  }

  return makeAxiosRequest(requestConfig, attr);
};

export const getData = async (
  url: string,
  attr?: FetchAttr,
  queryParams?: { [key: string]: string }
) => {
  const location = await retrieveUserLocationCookieData(attr);
  const token = attr?.authRequired
    ? await retrieveUserTokenCookieData(attr)
    : null;

  const requestConfig = {
    ...requestParams(attr?.authRequired, token),
    url: requestUrl(
      url,
      location?.name,
      location?.latitude,
      location?.longitude,
      attr,
      queryParams
    ),
    method: 'GET'
  };

  if (attr?.useCache) {
    return fetchCache(requestConfig, () =>
      makeAxiosRequest(requestConfig, attr)
    );
  }

  return makeAxiosRequest(requestConfig, attr);
};

export const generateUTMParams = (
  utm_source: string,
  utm_medium: string,
  utm_campaign: string,
  utm_content: string
) => {
  return (
    (utm_source || utm_medium || utm_campaign || utm_content) && {
      utm: {
        source: utm_source || null,
        medium: utm_medium || null,
        campaign: utm_campaign || null,
        content: utm_content || null
      }
    }
  );
};
